import {
  AnalyticsData,
  CompaniesApi,
  CompaniesWithFilter,
  CompanyFilterInfoApi,
  CompanyInfoApi,
  CompanyListInputApi,
  CompanyMetricsRequest,
  CompanyWithBlueprintInfo,
} from "../api/Companies";
import {
  blueprintsToBlueprintsInfo,
  CompaniesWithBlueprintInput,
} from "../api/CompaniesApi";
import { buildMockPromise } from "../api/helpers";

export const companiesList: CompanyFilterInfoApi[] = [
  {
    company_uuid: "comp1f1f-4408-44ec-9fa4-db4b17fdbf60",
    name: "Company 1",
    blueprint: ["b13d017f-01dc-48e8-ab5b-f59efc6cf353"],
    workflow: [],
  },
  {
    company_uuid: "comp2f1f-4408-44ec-9fa4-db4b17fdbf60",
    name: "Company 2",
    blueprint: [
      "b13d017f-01dc-48e8-ab5b-f59efc6cf353",
      "b23d017f-01dc-48e8-ab5b-f59efc6cf353",
    ],
  },
  {
    company_uuid: "comp3f1f-4408-44ec-9fa4-db4b17fdbf60",
    parent_company_uuid: "comp1f1f-4408-44ec-9fa4-db4b17fdbf60",
    name: "Company 3",
    blueprint: [],
  },
  {
    company_uuid: "22768ec2-fbbe-4882-8c05-1b10a994aff1",
    name: "Nicholson PLC",
    blueprint: ["b23d017f-01dc-48e8-ab5b-f59efc6cf353"],
  },
];

export async function getCompaniesApiMock(): Promise<CompaniesApi[]> {
  const companies = [
    {
      id: 101,
      external_id: "22768ec2-fbbe-4882-8c05-1b10a994aff1",
      is_deleted: false,
      parent_company_id: 0,
      name: "Nicholson PLC",
      notes: "Success.",
      is_active: true,
      review: true,
      template: "",
      chat_support: true,
      instant_fit_subscriber: false,
      consent_forms: false,
    },
    {
      id: 102,
      external_id: "d2765372-3b07-48ef-a6c6-49a2cc0261f4",
      is_deleted: false,
      parent_company_id: 0,
      name: "Scott, Gibson and Ramsey",
      notes: "Culture.",
      is_active: true,
      review: true,
      template: "",
      chat_support: true,
      instant_fit_subscriber: false,
      consent_forms: false,
    },
    {
      id: 103,
      external_id: "2d3877ee-35ff-4501-80d8-cbaecca6d68d",
      is_deleted: false,
      parent_company_id: 0,
      name: "Jenkins Inc",
      notes: "Decision.",
      is_active: true,
      review: true,
      template: "",
      chat_support: true,
      instant_fit_subscriber: false,
      consent_forms: false,
    },
    {
      id: 104,
      external_id: "b4a723d9-a49c-4374-b073-a4c64a44242c",
      is_deleted: false,
      parent_company_id: 0,
      name: "Baker-Lopez",
      notes: "Why until.",
      is_active: true,
      review: true,
      template: "",
      chat_support: true,
      instant_fit_subscriber: false,
      consent_forms: false,
    },
    {
      id: 105,
      external_id: "662ba046-1322-47bc-979b-df4e3de797b5",
      is_deleted: false,
      parent_company_id: 0,
      name: "Aguilar and Sons",
      notes: "Might.",
      is_active: true,
      review: true,
      template: "",
      chat_support: true,
      instant_fit_subscriber: false,
      consent_forms: false,
    },
  ];

  return buildMockPromise<CompaniesApi[]>(companies);
}

export async function getAllCompaniesListMock(): Promise<
  CompanyFilterInfoApi[]
> {
  return await buildMockPromise<CompanyFilterInfoApi[]>(companiesList);
}

function buildCompanyApiListMock(numItems: number): CompanyFilterInfoApi[] {
  const list: CompanyFilterInfoApi[] = [];
  for (let i = 1; i <= numItems; i++) {
    if (i >= 1 && i < 4) {
      list.push(companiesList[i - 1]);
    } else {
      list.push({
        company_uuid: `use${i}f-4408-44ec-9fa4-db4b17fdbf60`,
        name: `Company ${i}`,
        blueprint: [
          "b13d017f-01dc-48e8-ab5b-f59efc6cf353",
          "b3uef1f-4408-44ec-9fa4-db4b17fdbf63",
        ],
      });
    }
  }
  return list;
}

export async function getCompanyListMock(
  input: CompanyListInputApi
): Promise<CompaniesWithFilter> {
  const numberOfMockedItems = 100;
  let list = buildCompanyApiListMock(numberOfMockedItems);
  const currentIndex = (input.page - 1) * input.size;
  list = list.slice(currentIndex, currentIndex + input.size);
  let result: CompaniesWithFilter = {
    items: list,
    page: currentIndex + 1,
    total: numberOfMockedItems,
    size: input.size,
  };

  if (input.company_ids?.length) {
    const subCompany = companiesList.at(2);
    if (subCompany)
      return await buildMockPromise<CompaniesWithFilter>({
        ...result,
        items: [subCompany],
      });
  }
  return await buildMockPromise<CompaniesWithFilter>(result);
}

export async function getCompaniesWithBlueprintInfoMock(
  input: CompaniesWithBlueprintInput
): Promise<CompanyWithBlueprintInfo[]> {
  const result: CompanyWithBlueprintInfo[] = companiesList.map((company) => {
    return {
      company_uuid: company.company_uuid ?? "",
      name: company.name,
      blueprintsInfo: blueprintsToBlueprintsInfo(
        company.blueprint ?? [],
        input.blueprintsList
      ),
      client_qa: false,
      internal_qa: false,
      cqa_autocomplete_days: 0,
      auto_run_instant_fit: false,
    };
  });
  const filteredResult = result.filter((company) =>
    input.company_uuids.includes(company.company_uuid)
  );
  return await buildMockPromise<CompanyWithBlueprintInfo[]>(filteredResult);
}

export async function getBlueprintVerifyMock(
  desiredResponse: string[]
): Promise<string[]> {
  return await buildMockPromise<string[]>(desiredResponse);
}

export async function getCompanyInfoListMock(
  list: string[]
): Promise<CompanyInfoApi[]> {
  return await buildMockPromise<CompanyInfoApi[]>([
    {
      company_uuid: "da918817-4a72-4905-b481-eee315b55da2",
      name: "acron comp",
      blueprints: [],
      company_settings: [
        {
          company_setting_uuid: "a4b4ff34-3105-49c4-a48a-69752b60890c",
          setting_type_uuid: "a4b4ff34-3105-49c4-a48a-69752b60890c",
          name: "LOOK_BACK_PERIOD",
          value: "12",
          data_type: "INT",
        },
        {
          company_setting_uuid: "a4b4ff34-3105-49c4-a48a-69752b60890c",
          setting_type_uuid: "3faed8d9-af9d-41b4-9fb9-cc2cbd6e05ad",
          name: "INTERCOM_ACCESS",
          value: "True",
          data_type: "BOOL",
        },
        {
          company_setting_uuid: "a4b4ff34-3105-49c4-a48a-69752b60890c",
          setting_type_uuid: "7336dcc6-7942-4f98-b530-4bb7c592ec86",
          name: "SHOW_IMAGE_IN_REPORT",
          value: "True",
          data_type: "BOOL",
        },
        {
          company_setting_uuid: "a4b4ff34-3105-49c4-a48a-69752b60890c",
          setting_type_uuid: "e7fa9a6c-d770-4230-bb82-6df5bd5268c4",
          name: "USER_CREATION_LIMIT",
          value: "12",
          data_type: "INT",
        },
        {
          company_setting_uuid: "a4b4ff34-3105-49c4-a48a-69752b60890c",
          setting_type_uuid: "037bd401-424b-4a7f-84cb-82e5de616e13",
          name: "SUBCOMPANY_CREATION_LIMIT",
          value: "12",
          data_type: "INT",
        },
        {
          company_setting_uuid: "a4b4ff34-3105-49c4-a48a-69752b60890c",
          setting_type_uuid: "2026323a-17e8-48a0-a96d-3e26fe7ff214",
          name: "BLUEPRINT_CREATION_LIMIT",
          value: "12",
          data_type: "INT",
        },
        {
          company_setting_uuid: "a4b4ff34-3105-49c4-a48a-69752b60890c",
          setting_type_uuid: "21a17448-224b-4b2a-ae8c-6e90c5792f1a",
          name: "CAN_CREATE_COMPANY",
          value: "True",
          data_type: "BOOL",
        },
      ],
      review: true,
      template: "",
      chat_support: true,
      instant_fit_subscriber: false,
      consent_forms: false,
    },
  ]);
}

export async function getAnalyticsDataMock(
  input: CompanyMetricsRequest
): Promise<AnalyticsData> {
  return await buildMockPromise<AnalyticsData>({
    adverse_flag_dist: {
      "0": 212,
      "1": 80,
      "2": 56,
      "3": 34,
      "4": 20,
      "5": 12,
      "6": 8,
      "7": 14,
      "8": 5,
      "9": 8,
      "10": 5,
      "11": 4,
      "12": 5,
      "13": 7,
      "14": 5,
      "15": 4,
      "16": 6,
      "17": 2,
      "18": 2,
      "19": 3,
      "20": 3,
      "21": 2,
      "23": 1,
      "25": 2,
      "27": 1,
      "28": 1,
      "29": 1,
      "33": 1,
      "35": 2,
      "36": 1,
      "37": 2,
      "39": 3,
      "45": 2,
      "47": 1,
      "52": 2,
      "55": 1,
      "62": 1,
      "69": 1,
      "75": 1,
      "265": 1,
      "270": 1,
      "283": 1,
      "384": 1,
    },
    adverse_flag_stats: {
      avg: 6.824762,
      max: 384,
      median: 1,
      min: 0,
      percentile_10: 0,
      percentile_20: 0,
      percentile_30: 0,
      percentile_40: 0,
      percentile_50: 1,
      percentile_60: 2,
      percentile_70: 3,
      percentile_80: 6,
      percentile_85: 9,
      percentile_90: 14,
      percentile_95: 25,
      percentile_99: 67,
      percentile_99_99: 379,
    },
    candidate: {
      flag_count: 22,
      percentile: 95,
      total_candidates: 525,
    },
    chart_ready_data: {
      percentile_line_position: 95,
      highlight_flag_value: 22,
      distribution_points: [
        {
          flag_count: 0,
          candidate_count: 212,
          cumulative_percent: 40,
        },
        {
          flag_count: 1,
          candidate_count: 80,
          cumulative_percent: 56,
        },
        {
          flag_count: 2,
          candidate_count: 56,
          cumulative_percent: 66,
        },
        {
          flag_count: 3,
          candidate_count: 34,
          cumulative_percent: 73,
        },
        {
          flag_count: 4,
          candidate_count: 20,
          cumulative_percent: 77,
        },
        {
          flag_count: 5,
          candidate_count: 12,
          cumulative_percent: 79,
        },
        {
          flag_count: 6,
          candidate_count: 8,
          cumulative_percent: 80,
        },
        {
          flag_count: 7,
          candidate_count: 14,
          cumulative_percent: 83,
        },
        {
          flag_count: 8,
          candidate_count: 5,
          cumulative_percent: 84,
        },
        {
          flag_count: 9,
          candidate_count: 8,
          cumulative_percent: 86,
        },
        {
          flag_count: 10,
          candidate_count: 5,
          cumulative_percent: 86,
        },
        {
          flag_count: 11,
          candidate_count: 4,
          cumulative_percent: 87,
        },
        {
          flag_count: 12,
          candidate_count: 5,
          cumulative_percent: 88,
        },
        {
          flag_count: 13,
          candidate_count: 7,
          cumulative_percent: 90,
        },
        {
          flag_count: 14,
          candidate_count: 5,
          cumulative_percent: 90,
        },
        {
          flag_count: 15,
          candidate_count: 4,
          cumulative_percent: 91,
        },
        {
          flag_count: 16,
          candidate_count: 6,
          cumulative_percent: 92,
        },
        {
          flag_count: 17,
          candidate_count: 2,
          cumulative_percent: 93,
        },
        {
          flag_count: 18,
          candidate_count: 2,
          cumulative_percent: 93,
        },
        {
          flag_count: 19,
          candidate_count: 3,
          cumulative_percent: 94,
        },
        {
          flag_count: 20,
          candidate_count: 3,
          cumulative_percent: 94,
        },
        {
          flag_count: 21,
          candidate_count: 2,
          cumulative_percent: 95,
        },
        {
          flag_count: 23,
          candidate_count: 1,
          cumulative_percent: 95,
        },
        {
          flag_count: 25,
          candidate_count: 2,
          cumulative_percent: 95,
        },
        {
          flag_count: 27,
          candidate_count: 1,
          cumulative_percent: 95,
        },
        {
          flag_count: 28,
          candidate_count: 1,
          cumulative_percent: 96,
        },
        {
          flag_count: 29,
          candidate_count: 1,
          cumulative_percent: 96,
        },
        {
          flag_count: 33,
          candidate_count: 1,
          cumulative_percent: 96,
        },
        {
          flag_count: 35,
          candidate_count: 2,
          cumulative_percent: 96,
        },
        {
          flag_count: 36,
          candidate_count: 1,
          cumulative_percent: 97,
        },
        {
          flag_count: 37,
          candidate_count: 2,
          cumulative_percent: 97,
        },
        {
          flag_count: 39,
          candidate_count: 3,
          cumulative_percent: 98,
        },
        {
          flag_count: 45,
          candidate_count: 2,
          cumulative_percent: 98,
        },
        {
          flag_count: 47,
          candidate_count: 1,
          cumulative_percent: 98,
        },
        {
          flag_count: 52,
          candidate_count: 2,
          cumulative_percent: 98,
        },
        {
          flag_count: 55,
          candidate_count: 1,
          cumulative_percent: 99,
        },
        {
          flag_count: 62,
          candidate_count: 1,
          cumulative_percent: 99,
        },
        {
          flag_count: 69,
          candidate_count: 1,
          cumulative_percent: 99,
        },
        {
          flag_count: 75,
          candidate_count: 1,
          cumulative_percent: 99,
        },
        {
          flag_count: 265,
          candidate_count: 1,
          cumulative_percent: 99,
        },
        {
          flag_count: 270,
          candidate_count: 1,
          cumulative_percent: 100,
        },
        {
          flag_count: 283,
          candidate_count: 1,
          cumulative_percent: 100,
        },
        {
          flag_count: 384,
          candidate_count: 1,
          cumulative_percent: 100,
        },
      ],
    },
    company_name: "Lower Env Dummy Company",
    company_uuid: "75a32c6b-feb4-41f9-8429-410d6d5d5af6",
    comparison: {
      candidates_with_fewer: {
        count: 497,
        percentage: 95,
      },
      candidates_with_more: {
        count: 28,
        percentage: 5,
      },
      candidates_with_same: {
        count: 0,
        percentage: 0,
      },
    },
    metadata: {
      end_date: "2025-09-11",
      start_date: "2024-11-02",
    },
    report_total: 525,
  });
}

export async function getAnalyticsDataMockMultiDistribution(
  input: CompanyMetricsRequest
): Promise<AnalyticsData> {
  return await buildMockPromise<AnalyticsData>({
    adverse_flag_dist: {
      "0": 212,
      "1": 80,
      "2": 56,
      "3": 34,
      "4": 20,
      "5": 12,
      "6": 8,
      "7": 14,
      "8": 5,
      "9": 8,
      "10": 5,
      "11": 4,
      "12": 5,
      "13": 7,
      "14": 5,
      "15": 4,
      "16": 6,
      "17": 2,
      "18": 2,
      "19": 3,
      "20": 3,
      "21": 2,
      "23": 1,
      "25": 2,
    },
    adverse_flag_stats: {
      adverse_flag_cutoff: 25,
      avg: 6.824762,
      max: 384,
      median: 80,
      min: 0,
      percentile_10: 212,
      percentile_20: 0,
      percentile_30: 0,
      percentile_40: 0,
      percentile_50: 80,
      percentile_60: 56,
      percentile_70: 34,
      percentile_80: 40,
      percentile_85: 27,
      percentile_90: 26,
      percentile_95: 23,
      percentile_99: 21,
      percentile_99_99: 6,
    },
    candidate: {
      flag_count: 15,
      percentile: 96,
      total_candidates: 500,
    },
    chart_ready_data: {
      percentile_line_position: 96,
      highlight_flag_value: 15,
      distribution_points: [
        {
          flag_count: 0,
          candidate_count: 212,
          cumulative_percent: 42,
        },
        {
          flag_count: 1,
          candidate_count: 80,
          cumulative_percent: 58,
        },
        {
          flag_count: 2,
          candidate_count: 56,
          cumulative_percent: 70,
        },
        {
          flag_count: 3,
          candidate_count: 34,
          cumulative_percent: 76,
        },
        {
          flag_count: 4,
          candidate_count: 20,
          cumulative_percent: 80,
        },
        {
          flag_count: 5,
          candidate_count: 12,
          cumulative_percent: 83,
        },
        {
          flag_count: 6,
          candidate_count: 8,
          cumulative_percent: 84,
        },
        {
          flag_count: 7,
          candidate_count: 14,
          cumulative_percent: 87,
        },
        {
          flag_count: 8,
          candidate_count: 5,
          cumulative_percent: 88,
        },
        {
          flag_count: 9,
          candidate_count: 8,
          cumulative_percent: 90,
        },
        {
          flag_count: 10,
          candidate_count: 5,
          cumulative_percent: 91,
        },
        {
          flag_count: 11,
          candidate_count: 4,
          cumulative_percent: 92,
        },
        {
          flag_count: 12,
          candidate_count: 5,
          cumulative_percent: 93,
        },
        {
          flag_count: 13,
          candidate_count: 7,
          cumulative_percent: 94,
        },
        {
          flag_count: 14,
          candidate_count: 5,
          cumulative_percent: 95,
        },
        {
          flag_count: 15,
          candidate_count: 4,
          cumulative_percent: 96,
        },
        {
          flag_count: 16,
          candidate_count: 6,
          cumulative_percent: 97,
        },
        {
          flag_count: 17,
          candidate_count: 2,
          cumulative_percent: 97,
        },
        {
          flag_count: 18,
          candidate_count: 2,
          cumulative_percent: 98,
        },
        {
          flag_count: 19,
          candidate_count: 3,
          cumulative_percent: 98,
        },
        {
          flag_count: 20,
          candidate_count: 3,
          cumulative_percent: 99,
        },
        {
          flag_count: 21,
          candidate_count: 2,
          cumulative_percent: 99,
        },
        {
          flag_count: 23,
          candidate_count: 1,
          cumulative_percent: 100,
        },
        {
          flag_count: 25,
          candidate_count: 2,
          cumulative_percent: 100,
        },
      ],
    },
    company_name: "TikTok",
    company_uuid: "75a32c6b-feb4-41f9-8429-410d6d5d5af6",
    comparison: {
      candidates_with_fewer: {
        count: 475,
        percentage: 95,
      },
      candidates_with_more: {
        count: 21,
        percentage: 4,
      },
      candidates_with_same: {
        count: 4,
        percentage: 1,
      },
    },
    metadata: {
      end_date: "2025-10-01",
      start_date: "2024-11-02",
    },
    report_total: 525,
  });
}

export async function getAnalyticsDataMockSkewed(
  input: CompanyMetricsRequest
): Promise<AnalyticsData> {
  return await buildMockPromise<AnalyticsData>({
    adverse_flag_dist: {
      "0": 68,
      "1": 1,
      "2": 2,
      "3": 1,
    },
    adverse_flag_stats: {
      adverse_flag_cutoff: 15,
      avg: 0.111111,
      max: 3,
      median: 0,
      min: 0,
      percentile_10: 68,
      percentile_20: 0,
      percentile_30: 0,
      percentile_40: 0,
      percentile_50: 0,
      percentile_60: 0,
      percentile_70: 0,
      percentile_80: 0,
      percentile_85: 0,
      percentile_90: 0,
      percentile_95: 0,
      percentile_99: 3,
      percentile_99_99: 1,
    },
    candidate: {
      flag_count: 2,
      percentile: 99,
      total_candidates: 72,
    },
    chart_ready_data: {
      percentile_line_position: 99,
      highlight_flag_value: 2,
      distribution_points: [
        {
          flag_count: 0,
          candidate_count: 68,
          cumulative_percent: 94,
        },
        {
          flag_count: 1,
          candidate_count: 1,
          cumulative_percent: 96,
        },
        {
          flag_count: 2,
          candidate_count: 2,
          cumulative_percent: 99,
        },
        {
          flag_count: 3,
          candidate_count: 1,
          cumulative_percent: 100,
        },
      ],
    },
    company_name: "Lenexa PD",
    company_uuid: "53f30acd-5901-412d-9ebe-be59dcb031fd",
    comparison: {
      candidates_with_fewer: {
        count: 69,
        percentage: 96,
      },
      candidates_with_more: {
        count: 1,
        percentage: 1,
      },
      candidates_with_same: {
        count: 2,
        percentage: 3,
      },
    },
    metadata: {
      end_date: "2025-10-01",
      start_date: "2024-10-07",
    },
    report_total: 72,
  });
}
